import { CHECKOUT_MARKER_MAX_MS, checkoutReturnClaimUntil, claimGuestIdentity } from "../../lib/guestSession";
import { isCheckoutRef, readCheckoutRecord } from "./checkoutRecord";
import { holdPageScrubToken } from "../../../../../shared/errorReporting/heldTokens";

/**
 * THE CHECKOUT RETURN PAGE'S BOOT (design §2.5 (1); Fix 3). Run by main.tsx
 * at module evaluation, behind PAYMENTS_ENABLED, when the path is
 * `/checkout/return`: before the router renders, before App Check, reCAPTCHA
 * (after first paint) or any analytics.
 *
 * - The checkout reference is read from the URL (validated), else from this
 *   tab's record (H3), and `ref` and `result` are scrubbed from the address
 *   bar with `history.replaceState`. `result` is never read.
 * - A QUIET claim keeps the identity for the first reads: bounded by
 *   `checkoutReturnClaimUntil` (the latest well-formed marker while the
 *   identity is unknown, the ceiling while a retirement is pending, at least
 *   60 s; none when the retirement may not wait). It publishes no end and
 *   records no activity (R2). Once the identity is known it is retaken
 *   against THAT identity's marker; it is released on a terminal read.
 * - Nothing here mints an identity.
 */
export const CHECKOUT_RETURN_PATH = "/checkout/return";

/**
 * The paths React Router serves the page on: it matches case-insensitively
 * and accepts a trailing slash, so the boot must scrub every one of them
 * (security code review R-3).
 */
export function isCheckoutReturnPath(pathname: string): boolean {
  return /^\/checkout\/return\/?$/i.test(pathname);
}

let bootRef: string | null = null;
let release: (() => void) | null = null;
/** Released on a terminal read: never taken again on this page (R-1). */
let settled = false;

function takeClaim(identity: number | null, now: number): void {
  release?.();
  release = null;
  if (settled) return;
  const until = checkoutReturnClaimUntil(identity, now);
  if (until > now) release = claimGuestIdentity(until, CHECKOUT_MARKER_MAX_MS, { quiet: true });
}

export function bootCheckoutReturn(now: number = Date.now()): void {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("ref");
  bootRef = isCheckoutRef(fromUrl) ? fromUrl : (readCheckoutRecord(now)?.ref ?? null);
  // The ref leaves the address bar below, so reports scrub it as held (R-8).
  if (bootRef !== null) holdPageScrubToken(bootRef);
  if (url.searchParams.has("ref") || url.searchParams.has("result")) {
    url.searchParams.delete("ref");
    url.searchParams.delete("result");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }
  takeClaim(null, now);
}

/** The reference the boot found, or null. */
export function checkoutReturnRef(): string | null {
  return bootRef;
}

/** The identity is known now: the claim follows ITS marker (§2.5). */
export function retakeCheckoutReturnClaim(identity: number | null, now: number = Date.now()): void {
  takeClaim(identity, now);
}

/**
 * A terminal read, or the page going away. FINAL for this page: a later
 * identity change (PAID retires the identity, then the user is null) must not
 * take a new claim (security code review R-1).
 */
export function releaseCheckoutReturnClaim(): void {
  settled = true;
  release?.();
  release = null;
}

/** Test-only. */
export function __resetCheckoutReturnBootForTests(): void {
  releaseCheckoutReturnClaim();
  settled = false;
  bootRef = null;
}
